// src/components/ColissimoParams.js
// Étiquettes retour Colissimo — /colissimo-params, opened from the Colissimo
// carrier page. The Colissimo contract (number + password) and the return
// address printed on the labels the ticket sidebar creates.
// API: GET/POST/PUT/DELETE /api/colissimo_settings/settings (404 = none yet).
// The GET answers the stored password in clear: it never reaches React state
// or the DOM here (« enregistré » + replace); a PUT without a new password
// leaves out the field, which the backend keeps (None values are skipped).

import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ChevronLeft, Eye, EyeOff, MapPin, Pencil, Phone, ShieldCheck, Tag, Trash2 } from 'lucide-react';
import {
  Badge, Banner, Card, ConfirmDialog, DescriptionList, EmptyState, Field, OverflowMenu, PageHeader, Toast,
} from '../design/ui';
import { formatShortDateTime } from '../utils/dates';
import './ColissimoParams.css';

const FIELDS = [
  'contract_number', 'return_company_name', 'return_last_name', 'return_first_name', 'return_line1', 'return_line2',
  'return_city', 'return_zip_code', 'return_country_code', 'return_phone_number', 'return_email',
];

const getEmptyFormData = () => ({
  contract_number: '',
  password: '',
  return_company_name: '',
  return_last_name: '',
  return_first_name: '',
  return_line1: '',
  return_line2: '',
  return_city: '',
  return_zip_code: '',
  return_country_code: 'FR',
  return_phone_number: '',
  return_email: '',
});

const COUNTRIES = [
  ['FR', 'France'], ['BE', 'Belgique'], ['DE', 'Allemagne'], ['ES', 'Espagne'], ['IT', 'Italie'], ['CH', 'Suisse'], ['LU', 'Luxembourg'],
];

// The stored settings without the secret: { ...fields, has_password }.
export const withoutSecret = (data) => {
  if (!data) return null;
  const { password, ...rest } = data;
  return { ...rest, has_password: Boolean(password) };
};

const ColissimoParams = () => {
  const { t } = useTranslation();
  const [settings, setSettings] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState(getEmptyFormData);
  const [toast, setToast] = useState(null);
  const [formError, setFormError] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const headers = () => ({ Authorization: `Bearer ${localStorage.getItem('access_token')}` });

  const formFrom = (stored) => ({
    ...getEmptyFormData(),
    ...Object.fromEntries(FIELDS.map((key) => [key, stored?.[key] ?? (key === 'return_country_code' ? 'FR' : '')])),
    password: '',
  });

  const fetchSettings = useCallback(async () => {
    try {
      setLoading(true);
      setLoadError(null);
      const response = await axios.get('/api/colissimo_settings/settings', { headers: headers() });
      const stored = withoutSecret(response.data);
      setSettings(stored);
      setFormData(formFrom(stored));
      setIsCreating(false);
    } catch (error) {
      if (error.response?.status === 404) {
        // No settings yet: the contract is entered right away.
        setSettings(null);
        setIsCreating(true);
      } else {
        console.error('Failed to fetch Colissimo settings:', error?.response?.status || error?.message);
        setLoadError(t('colissimoParams.failedToLoad', 'Échec du chargement des paramètres Colissimo.'));
      }
    } finally {
      setLoading(false);
    }
  }, [t]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError(null);
    try {
      setSaving(true);
      if (isCreating) {
        const response = await axios.post('/api/colissimo_settings/settings', formData, { headers: headers() });
        setSettings(withoutSecret(response.data));
        setFormData(formFrom(response.data));
        setIsCreating(false);
        setToast({ tone: 'success', text: t('colissimoParams.settingsCreatedSuccess', 'Paramètres Colissimo créés avec succès !') });
      } else {
        const payload = Object.fromEntries(FIELDS.map((key) => [key, formData[key]]));
        // An empty password keeps the stored one (never sent back from here).
        if (formData.password) payload.password = formData.password;
        const response = await axios.put('/api/colissimo_settings/settings', payload, { headers: headers() });
        setSettings(withoutSecret(response.data));
        setFormData(formFrom(response.data));
        setIsEditing(false);
        setToast({ tone: 'success', text: t('colissimoParams.settingsUpdatedSuccess', 'Paramètres Colissimo mis à jour avec succès !') });
      }
    } catch (error) {
      console.error('Failed to save Colissimo settings:', error?.response?.status || error?.message);
      const detail = error.response?.data?.detail;
      setFormError(typeof detail === 'string' ? detail : t('colissimoParams.failedToSave', 'Échec de l’enregistrement des paramètres Colissimo.'));
    } finally {
      setSaving(false);
      setShowPassword(false);
    }
  };

  const handleDelete = async () => {
    try {
      setSaving(true);
      await axios.delete('/api/colissimo_settings/settings', { headers: headers() });
      setSettings(null);
      setIsCreating(true);
      setIsEditing(false);
      setFormData(getEmptyFormData());
      setToast({ tone: 'success', text: t('colissimoParams.settingsDeletedSuccess', 'Paramètres Colissimo supprimés avec succès !') });
    } catch (error) {
      console.error('Failed to delete Colissimo settings:', error?.response?.status || error?.message);
      setToast({ tone: 'danger', text: t('colissimoParams.failedToDelete', 'Échec de la suppression des paramètres Colissimo.') });
    } finally {
      setSaving(false);
      setConfirmDelete(false);
    }
  };

  const handleCancel = () => {
    setFormError(null);
    if (settings) {
      setFormData(formFrom(settings));
      setIsEditing(false);
    } else {
      setFormData(getEmptyFormData());
      setIsCreating(false);
    }
  };

  const isFormEnabled = isEditing || isCreating;
  const countryName = (code) => COUNTRIES.find(([value]) => value === code)?.[1] || code;

  const textField = (name, label, { type = 'text', required = false, help, full = false } = {}) => (
    <div className={full ? 'colissimo-v2__full' : undefined}>
      <Field label={`${label}${required ? ' *' : ''}`} htmlFor={`colissimo-${name}`} help={help}>
        <input
          id={`colissimo-${name}`}
          name={name}
          type={type}
          className="u-input"
          value={formData[name]}
          onChange={handleInputChange}
          required={required}
        />
      </Field>
    </div>
  );

  const renderForm = () => (
    <form onSubmit={handleSubmit} className="colissimo-v2__form">
      <Card
        icon={isCreating ? Tag : Pencil}
        title={isCreating
          ? t('carriersUi.colissimo.createTitle', 'Enregistrer le contrat Colissimo')
          : t('carriersUi.colissimo.editTitle', 'Modifier les paramètres Colissimo')}
        description={t('carriersUi.colissimo.formDesc', 'Le compte qui signe les demandes d’étiquettes, et l’adresse où reviennent les colis.')}
        footer={(
          <>
            {(settings || isEditing) && (
              <button type="button" className="u-btn u-btn--secondary" onClick={handleCancel} disabled={saving}>
                {t('colissimoParams.cancel', 'Annuler')}
              </button>
            )}
            <button type="submit" className="u-btn u-btn--primary" disabled={saving}>
              {saving
                ? t('colissimoParams.saving', 'Enregistrement...')
                : (isCreating ? t('carriersUi.colissimo.create', 'Enregistrer le contrat') : t('colissimoParams.saveChanges', 'Enregistrer les modifications'))}
            </button>
          </>
        )}
      >
        <div className="u-form">
          {formError && <Banner tone="danger">{formError}</Banner>}
          <div className="u-overline">{t('carriersUi.colissimo.contract', 'Contrat Colissimo')}</div>
          <div className="u-form-grid">
            {textField('contract_number', t('carriersUi.colissimo.contractNumber', 'Numéro de contrat'), { required: true })}
            <Field
              label={`${t('carriersUi.password', 'Mot de passe')}${isCreating ? ' *' : ''}`}
              htmlFor="colissimo-password"
              help={!isCreating && settings?.has_password
                ? t('carriersUi.colissimo.passwordKeep', 'Enregistré — laissez vide pour le garder. Il n’est jamais réaffiché.')
                : t('carriersUi.colissimo.passwordHelp', 'Le mot de passe associé au numéro de contrat.')}
            >
              <div className="u-input-group">
                <input
                  id="colissimo-password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  className="u-input"
                  autoComplete="new-password"
                  value={formData.password}
                  onChange={handleInputChange}
                  required={isCreating}
                  placeholder={!isCreating && settings?.has_password ? '••••••••' : ''}
                />
                <button
                  type="button"
                  className="u-input-group__btn"
                  onClick={() => setShowPassword(!showPassword)}
                  aria-label={showPassword ? t('colissimoParams.hidePassword', 'Masquer le mot de passe') : t('colissimoParams.showPassword', 'Afficher le mot de passe')}
                  title={showPassword ? t('colissimoParams.hidePassword', 'Masquer le mot de passe') : t('colissimoParams.showPassword', 'Afficher le mot de passe')}
                >
                  {showPassword ? <EyeOff /> : <Eye />}
                </button>
              </div>
            </Field>
          </div>

          <div className="u-overline colissimo-v2__section">{t('colissimoParams.returnAddress', 'Adresse de retour')}</div>
          <div className="u-form-grid">
            {textField('return_company_name', t('colissimoParams.companyName', 'Nom de l’entreprise'), { full: true })}
            {textField('return_first_name', t('colissimoParams.firstName', 'Prénom'))}
            {textField('return_last_name', t('colissimoParams.lastName', 'Nom'))}
            {textField('return_line1', t('colissimoParams.addressLine1', 'Adresse ligne 1'), { full: true })}
            {textField('return_line2', t('colissimoParams.addressLine2', 'Adresse ligne 2'), { full: true })}
            {textField('return_zip_code', t('colissimoParams.zipCode', 'Code postal'))}
            {textField('return_city', t('colissimoParams.city', 'Ville'))}
            <Field label={t('carriersUi.colissimo.country', 'Pays')} htmlFor="colissimo-return_country_code">
              <select id="colissimo-return_country_code" name="return_country_code" className="u-select" value={formData.return_country_code} onChange={handleInputChange}>
                {COUNTRIES.map(([code, name]) => <option key={code} value={code}>{`${t(`carriersUi.colissimo.countries.${code}`, name)} (${code})`}</option>)}
              </select>
            </Field>
          </div>

          <div className="u-overline colissimo-v2__section">{t('colissimoParams.returnContactInformation', 'Coordonnées de retour')}</div>
          <div className="u-form-grid">
            {textField('return_phone_number', t('colissimoParams.phoneNumber', 'Numéro de téléphone'), { type: 'tel' })}
            {textField('return_email', t('carriersUi.colissimo.email', 'E-mail'), { type: 'email' })}
          </div>
        </div>
      </Card>
    </form>
  );

  const renderView = () => (
    <div className="colissimo-v2__stack">
      <Card
        icon={ShieldCheck}
        title={t('carriersUi.colissimo.contract', 'Contrat Colissimo')}
        description={t('carriersUi.colissimo.contractDesc', 'Le compte qui signe les demandes d’étiquettes auprès de Colissimo.')}
      >
        <DescriptionList items={[
          { label: t('carriersUi.colissimo.contractNumber', 'Numéro de contrat'), value: <code className="u-code">{settings.contract_number}</code> },
          {
            label: t('carriersUi.password', 'Mot de passe'),
            value: settings.has_password
              ? <Badge tone="success" dot>{t('carriersUi.colissimo.passwordSaved', 'Enregistré')}</Badge>
              : <Badge tone="warning" dot>{t('carriersUi.colissimo.passwordMissing', 'Manquant')}</Badge>,
          },
          settings.updated_at && { label: t('carriersUi.colissimo.updatedAt', 'Mis à jour le'), value: formatShortDateTime(settings.updated_at) },
        ]}
        />
      </Card>
      <div className="u-grid-2">
        <Card
          icon={MapPin}
          title={t('colissimoParams.returnAddress', 'Adresse de retour')}
          description={t('colissimoParams.returnAddressDescription', 'Ces informations apparaissent comme expéditeur et adresse de retour sur les étiquettes.')}
        >
          <address className="colissimo-v2__address">
            {[
              settings.return_company_name,
              [settings.return_first_name, settings.return_last_name].filter(Boolean).join(' '),
              settings.return_line1,
              settings.return_line2,
              [settings.return_zip_code, settings.return_city].filter(Boolean).join(' '),
              countryName(settings.return_country_code),
            ].filter(Boolean).map((line) => <span key={line}>{line}</span>)}
          </address>
        </Card>
        <Card
          icon={Phone}
          title={t('colissimoParams.returnContactInformation', 'Coordonnées de retour')}
          description={t('colissimoParams.returnContactDescription', 'Coordonnées utilisées lorsque les transporteurs ont besoin des informations de l’expéditeur pour un retour.')}
        >
          <DescriptionList items={[
            { label: t('colissimoParams.phoneNumber', 'Numéro de téléphone'), value: settings.return_phone_number || <span className="u-muted">—</span> },
            { label: t('carriersUi.colissimo.email', 'E-mail'), value: settings.return_email || <span className="u-muted">—</span> },
          ]}
          />
        </Card>
      </div>
    </div>
  );

  const renderBody = () => {
    if (loading) {
      return <span className="u-skeleton colissimo-v2__skeleton" aria-busy="true" aria-label={t('colissimoParams.loading', 'Chargement des paramètres Colissimo...')} />;
    }
    if (loadError) return <Banner tone="danger">{loadError}</Banner>;
    if (!settings && !isCreating) {
      return (
        <section className="u-card">
          <EmptyState
            icon={Tag}
            title={t('colissimoParams.noSettingsConfigured', 'Aucun paramètre Colissimo configuré.')}
            action={(
              <button type="button" className="u-btn u-btn--primary u-btn--sm" onClick={() => setIsCreating(true)}>
                {t('carriersUi.colissimo.create', 'Enregistrer le contrat')}
              </button>
            )}
          >
            {t('colissimoParams.emptyDescription', 'Ajoutez vos informations de contrat une seule fois pour activer la génération d’étiquettes Colissimo.')}
          </EmptyState>
        </section>
      );
    }
    return isFormEnabled ? renderForm() : renderView();
  };

  const status = loading ? null : settings
    ? <Badge tone="success" dot>{t('colissimoParams.readyToUse', 'Prêt pour créer des étiquettes')}</Badge>
    : <Badge tone="warning" dot>{t('colissimoParams.setupRequired', 'Configuration requise')}</Badge>;

  return (
    <div className="u-page u-page--narrow colissimo-v2">
      <PageHeader
        breadcrumb={(
          <Link to="/carriers-instructions">
            <ChevronLeft className="colissimo-v2__crumb-icon" aria-hidden="true" />
            {t('carriersInstructions.pageTitle', 'Transporteurs')}
          </Link>
        )}
        title={t('carriersUi.colissimo.title', 'Étiquettes retour Colissimo')}
        subtitle={(
          <span className="colissimo-v2__subtitle">
            {t('carriersUi.colissimo.subtitle', 'Le contrat et l’adresse de retour des étiquettes créées depuis un ticket.')}
            {status}
          </span>
        )}
        actions={settings && !isFormEnabled ? (
          <>
            <OverflowMenu
              label={t('carriersUi.actions', 'Actions')}
              items={[{ label: t('colissimoParams.deleteSettings', 'Supprimer les paramètres'), icon: Trash2, danger: true, onClick: () => setConfirmDelete(true) }]}
            />
            <button type="button" className="u-btn u-btn--primary" onClick={() => setIsEditing(true)}>
              <Pencil aria-hidden="true" />
              {t('carriersUi.detail.edit', 'Modifier')}
            </button>
          </>
        ) : null}
      />
      <div className="u-well">
        {renderBody()}
      </div>

      {confirmDelete && (
        <ConfirmDialog
          title={t('carriersUi.colissimo.deleteTitle', 'Supprimer le contrat Colissimo {{number}} ?', { number: settings?.contract_number })}
          message={t('carriersUi.colissimo.deleteMessage', 'Plus aucune étiquette retour Colissimo ne pourra être créée depuis un ticket tant qu’un contrat n’est pas enregistré. Cette action est irréversible.')}
          confirmLabel={t('carriersUi.delete', 'Supprimer')}
          cancelLabel={t('colissimoParams.cancel', 'Annuler')}
          onConfirm={handleDelete}
          onCancel={() => setConfirmDelete(false)}
          busy={saving}
        />
      )}
      {toast && (
        <Toast tone={toast.tone} onClose={() => setToast(null)} closeLabel={t('common.close', 'Fermer')}>
          {toast.text}
        </Toast>
      )}
    </div>
  );
};

export default ColissimoParams;
